import { Elements, PaymentElement, useElements, useStripe as useStripeAPI } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useApp } from 'contexts/app';
import { useStripe } from 'contexts/stripe';
import { useViewport } from 'contexts/viewport';
import stripeService from 'services/stripe';
import { Button, Dialog, Form, IconButton, Stack, styled, Text } from 'shell/components';
import theme from 'shell/themes';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const stripeOptions = {
    appearance: {
        variables: {
            fontSizeBase: `${theme.typography.p2.fontSize.large}rem`,
        },
    },
    currency: 'usd',
    mode: 'setup',
};

const UpdateCardForm = () => {
    const { customer } = useApp();
    const elements = useElements();
    const stripeAPI = useStripeAPI();
    const { updateCardDialog, setUpdateCardDialog } = useStripe();

    const handleSubmit = async () => {
        if (!stripeAPI || !elements) return;
        setUpdateCardDialog((current) => ({ ...current, isLoading: true }));
        try {
            await elements.submit();
            const { client_secret: clientSecret } = await stripeService.createSetupIntent(customer.id, {
                action: updateCardDialog.reactivate
                    ? 'update_payment_method_and_undo_cancel_at_period_end'
                    : 'update_payment_method',
            });
            const { error } = await stripeAPI.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: window.location.href,
                },
            });
            if (error) throw error;
        } catch (e) {
            console.error(e);
            setUpdateCardDialog((current) => ({ ...current, isLoading: false }));
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Stack spacing={4}>
                <PaymentElement />
                <Stack direction={'row-reverse'} spacing={4}>
                    <Button loading={updateCardDialog.isLoading} type={'submit'}>
                        {updateCardDialog.reactivate ? 'Reactivate Subscription' : 'Save'}
                    </Button>
                    <Button
                        color={'neutral'}
                        variant={'tertiary'}
                        onClick={() => setUpdateCardDialog((current) => ({ ...current, open: false }))}
                    >
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </Form>
    );
};

const UpdateCardDialog = () => {
    const { updateCardDialog, setUpdateCardDialog } = useStripe();
    const { device } = useViewport();

    const onClose = () => {
        setUpdateCardDialog((current) => ({ ...current, open: false }));
    };

    return (
        <CustomDialog device={device} open={updateCardDialog.open} onClose={onClose}>
            <Stack spacing={5}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {updateCardDialog.reactivate ? 'Add' : 'Update'} Payment Details
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
                <Text>Payment details will be saved to your account.</Text>
                <Elements stripe={stripePromise} options={stripeOptions}>
                    <UpdateCardForm />
                </Elements>
            </Stack>
        </CustomDialog>
    );
};

const CustomDialog = styled(Dialog)(({ device }) => {
    return {
        '& .MuiDialog-paper > .MuiBox-root': {
            minHeight: '505px',
            ...(device !== 'mobile' && {
                width: '400px',
            }),
        },
    };
});

export default UpdateCardDialog;
